import React from 'react';
import invariant from 'tiny-invariant';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MoButton } from '@src/components/MoButton';
import { MoModal } from '@src/components/MoModal';
import { useSuccessFailureNotification } from '@src/hooks/useSuccessFailureNotification';
import { useChatsContext } from '../ChatsContext';

type DeleteChatModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function DeleteChatModal({ open, onClose }: DeleteChatModalProps) {
  const { chat, chatRootPath, deleteChatConfig } = useChatsContext();
  const history = useHistory();
  const { callWithNotification } = useSuccessFailureNotification();

  invariant(
    chat && deleteChatConfig,
    'DeleteChatModal should only render with a loaded chat and deleteChatConfig',
  );
  const { deleteChat, deleteChatLoading } = deleteChatConfig;

  const handleDelete = async () => {
    await callWithNotification(
      () => deleteChat(chat.id),
      'Chat deleted',
      'Failed to delete chat',
      () => history.push(chatRootPath),
    );
  };

  return (
    <MoModal
      open={open}
      onClose={onClose}
      title="Delete this chat?"
      actions={
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <MoButton variant="outlined" onClick={onClose} disabled={deleteChatLoading}>
            Cancel
          </MoButton>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleDelete}
            loading={deleteChatLoading}
          >
            Delete chat
          </LoadingButton>
        </Box>
      }
    >
      <Typography>
        This archived chat and its message history will no longer be visible to you or the other
        participants. This can’t be undone.
      </Typography>
    </MoModal>
  );
}
